import * as React from 'react'
import CollapsableCard, { CollapsableCardBody, CollapsableCardHeader } from '../../../components/CollapsableCard/CollapsableCard'
import { BELLRINGER_QUESTION_NUMBERS, BELLRINGER_TABLE_WEEK_NUMBERS, getBellringerSetNumber, getStatsForSetAndSubject } from '../../../services/bellringers'
import { NA } from '../../../constants'
import { ScantronBubble } from '../../Practice/components/ScantronBubble';
import { TeacherStore } from '../../../stores/teacher.store';


const iconCorrect = "/assets/images/icons/v2/ico-answer-status-correct.svg"
const iconIncorrect = "/assets/images/icons/v2/ico-answer-status-incorrect.svg"

interface Props {
    grades: number[]
    info: any
    onQuestionClick: (bellringerId: string, period: string) => void
}

interface State {

}

export default class BellringersPeriodSubview extends React.Component<Props, State> {
    render() {
        let {grades, info, onQuestionClick} = this.props
        let {subject, questionsStats, period} = info

        return ( 
            <div>
                { grades.map((grade, index) => {
                    let setNumber = getBellringerSetNumber(grade) // Grade 9 -> Set 1 (this is recently changed to be displayed as Set #s)
                    let title = `Set ${setNumber}`
                    let statsForSet = getStatsForSetAndSubject(questionsStats, setNumber, subject)

                    return (
                        <div className="bellringers-table bellringers-table--subject-view mb-2 pb-1">
                            <GradesViewForTeacher 
                                key={`bellringers-teacher-grades-card-${index}`}
                                subject={subject}
                                grade={grade}
                                period={period}
                                ribbonColor={subject}
                                setNumber={setNumber}
                                title={title}
                                collapsed={false}
                                stats={statsForSet}
                                onQuestionClick={onQuestionClick}
                            />
                        </div>
                    )}
                )}

            </div>
        )
    }
}


//--- Helpers ---

function GradesViewForTeacher({ subject, grade, period, key, setNumber,
    stats, 
    title, ribbonColor = "darkPurple", headerColor = "#279ed7", 
    collapsed = true, onQuestionClick }) {

    let hasData = stats && stats.length > 0

    return (
        <CollapsableCard key={key} initiallyCollapsed={collapsed}>
            <CollapsableCardHeader>
                <div className="lesson-list-card-header">
                    <span className={`ribbon ${ribbonColor}`}>
                        <span className="bellringers-grade-level">{setNumber}</span>
                    </span>
                    <h2 className="lesson-list-card-header--text">{title}</h2>
                </div>
            </CollapsableCardHeader>

            <CollapsableCardBody> 
                {hasData ? 
                    <div className="table-wrapper responsive-table-wrapper">
                     <BellringerTableForPeriods subject={subject} grade={grade} setNumber={setNumber} period={period} stats={stats} onQuestionClick={onQuestionClick}/>
                    </div>
                    :
                    <div className="mb-3"><h6 className="subcategory-title">NO QUESTIONS ANSWERED YET.</h6></div>
                }
            </CollapsableCardBody>
        </CollapsableCard>
    )
}

function getWeeklyStats(stats, setNumber, subject, week) {
    let foundAny = stats.filter(d => d.setNumber === setNumber && d.subject === subject && d.week === week)
    return foundAny
}

function getFilteredStats(stats, setNumber, subject, week, period, ) {
    return stats.filter(d => d.setNumber === setNumber && d.subject === subject && d.week === week && d.period === period)
}

function getQuestionStats(stats, questionNumber) {
    return stats.find(s => s.questionNumber === questionNumber)
}

function getAverageInfoFromQuestionStats(questionStats) {
    if (!questionStats) return ""
    return `${Math.round(100*questionStats.scoreAverage)}%`
}

export function BellringerTableForPeriods({ subject, setNumber, grade, stats, period, onQuestionClick }) {
    let weeks = BELLRINGER_TABLE_WEEK_NUMBERS
    let renderedIndex = -1

    return (
        <table>
            <thead className={`report-table-header ` + subject}>
                <tr>
                    <th>Week</th>
                    <th>Datapoint</th>
                    <th>Q1</th>
                    <th>Q2</th>
                    <th>Q3</th>
                </tr>
            </thead>

            <tbody>
                {weeks.map(week => {
                    let weeklyStats = getWeeklyStats(stats, setNumber, subject, week)

                    if (!weeklyStats || weeklyStats.length === 0) return ""

                    renderedIndex++

                    let questionNumbers = BELLRINGER_QUESTION_NUMBERS // [1, 2, 3]
                    let lessonName = TeacherStore.getBellringersWeeklyLessonName(subject, setNumber, week)

                    return (
                        <React.Fragment key={week}>
                            <tr className={renderedIndex % 2 === 1 ? 'week-alt' : undefined}>
                                <td rowSpan={5}>
                                    Week {week}
                                    <br/>
                                    <span className="bellringers-weekly-lesson-name">{lessonName}</span>
                                </td>
                                <td>Accuracy</td>
                                {questionNumbers.map(questionNumber => {
                                    const stats = getQuestionStats(weeklyStats, questionNumber)
                                    if (!stats) return <td key={`${week}-${questionNumber}-accuracy`} />
                                    const { scoreAverage } = stats
                                    return (
                                        <td key={`${week}-${questionNumber}-accuracy`}>
                                            {Math.round(100 * scoreAverage)}%
                                        </td>
                                    )
                                })}
                            </tr>

                            <tr className={renderedIndex % 2 === 1 ? 'week-alt' : undefined}>
                                <td>Avg. Time</td>
                                {questionNumbers.map(questionNumber => {
                                    const stats = getQuestionStats(weeklyStats, questionNumber)
                                    if (!stats) return <td key={`${week}-${questionNumber}-avgtime`} />
                                    const { studyTimeAverage } = stats
                                    return (
                                        <td key={`${week}-${questionNumber}-avgtime`}>
                                            {Math.round(studyTimeAverage)} seconds
                                        </td>
                                    )
                                })}
                            </tr>

                            <tr className={renderedIndex % 2 === 1 ? 'week-alt' : undefined}>
                                <td>Time Range</td>
                                {questionNumbers.map(questionNumber => {
                                    const stats = getQuestionStats(weeklyStats, questionNumber)
                                    if (!stats) return <td key={`${week}-${questionNumber}-timerange`} />
                                    const { studyTimeMin, studyTimeMax } = stats
                                    return (
                                        <td key={`${week}-${questionNumber}-timerange`}>
                                            {studyTimeMin}-{studyTimeMax} seconds
                                        </td>
                                    )
                                })}
                            </tr>

                            <tr className={renderedIndex % 2 === 1 ? 'week-alt' : undefined}>
                                <td># Student Answers</td>
                                {questionNumbers.map(questionNumber => {
                                    const stats = getQuestionStats(weeklyStats, questionNumber)
                                    if (!stats) return <td key={`${week}-${questionNumber}-count`} />
                                    const { answerTotal } = stats
                                    return (
                                        <td key={`${week}-${questionNumber}-count`}>
                                            {answerTotal} student{answerTotal !== 1 ? 's' : ''}
                                        </td>
                                    )
                                })}
                            </tr>

                            <tr className={renderedIndex % 2 === 1 ? 'week-alt' : undefined}>
                                <td>Answers</td>
                                {questionNumbers.map(questionNumber => {
                                    const stats = getQuestionStats(weeklyStats, questionNumber)
                                    if (!stats) return <td key={`${week}-${questionNumber}-answers`} />
                                    const bellringerId = `${subject}-${grade}-${week}-${questionNumber}`
                                    const levelOfDifficulty = TeacherStore.getBellringerLevelOfDifficulty(bellringerId)

                                    return (
                                        <td key={`${week}-${questionNumber}-answers`}>
                                            <div className="d-flex flex-wrap flex-column flex-xl-row justify-content-between align-items-start">
                                                <AnswerStats stats={stats} />
                                                <button
                                                    className="btn btn-bellringers-question-view-inline"
                                                    onClick={e => onQuestionClick(bellringerId, period)}>
                                                    View Q{questionNumber}
                                                    &nbsp;
                                                    <img
                                                        className="explanation-button-image"
                                                        src={`/assets/images/icons/v2/ico-modal.svg`}
                                                        width="18"
                                                        height="18"
                                                        alt=""
                                                    />
                                                    &nbsp;
                                                    <span className="bellringer-question-level-of-difficulty period-view">({levelOfDifficulty})</span>
                                                </button>
                                            </div>
                                        </td>
                                    )
                                })}
                            </tr>
                        </React.Fragment>
                    )}
                )}
            </tbody>
        </table>
    )
}

function getPercentage(val) {
    return Math.round(100*val)
}

function getPercentageInfo(val: number) {
    if (val < 0) return NA
    return `${getPercentage(val)}%`
}

function getProperIconUrl(correctAnswer: string, currentAnswerLabel: string) {
    return correctAnswer === currentAnswerLabel ? iconCorrect: iconIncorrect
}

function getAverageInfoForAnswer(stats, answer: string) {
    let {answerTotal, answerCountA, answerCountB, answerCountC, answerCountD} = stats
    let targetAnswer  = 
        answer === "A" ? answerCountA: 
        answer === "B" ? answerCountB:
        answer === "C" ? answerCountC:
        answer === "D" ? answerCountD: 0
    let average = targetAnswer / answerTotal

    return `${getPercentageInfo(average)}`
}

function AnswerStats(props) {
    let { stats } = props
    let { correctAnswer, answerTotal, distractorAnswer } = stats

    if (answerTotal <= 0) return null

    let answers = ["A", "B", "C", "D"]

    return (
        <div className="d-flex answerStats">
            {answers.map(answer => {
                let answerCount = stats[`answerCount${answer}`]
                let percentage = answerTotal > 0 ? (answerCount / answerTotal) * 100 : 0
                let averageInfo = getAverageInfoForAnswer(stats, answer)
                let isCorrectAnswer = answer === correctAnswer

                return (
                    <div key={answer} className="text-center">
                        <ScantronBubble
                            letter={answer}
                            userAnswer={(percentage > 0 || isCorrectAnswer) ? answer : undefined}
                            correctAnswer={correctAnswer}
                            disabled={true}
                        />
                        <div className="pt-3 mb-3">{averageInfo}</div>
                    </div>
                )
            })}
        </div>
    )
}

function AnswerStatsX(props) {
    let {stats} = props
    let {correctAnswer, answerTotal, answerCountA, answerCountB, answerCountC, answerCountD, distractorAnswer} = stats
    
    if (answerTotal <= 0) return null

    let averageA = answerCountA/answerTotal
    let averageB = answerCountB/answerTotal
    let averageC = answerCountC/answerTotal
    let averageD = answerCountD/answerTotal

    let infoAnswerA = `${getPercentageInfo(averageA)}`
    let infoAnswerB = `${getPercentageInfo(averageB)}`
    let infoAnswerC = `${getPercentageInfo(averageC)}`
    let infoAnswerD = `${getPercentageInfo(averageD)}`
    let distractorAnswerFound = distractorAnswer !== NA ? true: false
    let infoDistractorAnswer = `${distractorAnswerFound ? distractorAnswer: ""}`
    let answers = ["A", "B", "C", "D"]

    return (
        <div>
            {answers.map(answer => {
                let correct = correctAnswer === answer
                let imgSrc = correct ? iconCorrect : iconIncorrect
                let imgAlt = correct ? "Correct" : "Incorrect"

                return (
                    <div key={answer}>
                        {answer}: <img src={imgSrc} alt={imgAlt} />
                        {infoDistractorAnswer}
                    </div>
                )
            })}
        </div>
    )
}